<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Si eres nuevo en los tours virtuales y te preguntas cómo crear
            experiencias 360° profesionales desde tu teléfono, estás en el lugar
            adecuado. Esta guía completa para principiantes desgrana todo lo que
            necesitas saber — sin conocimientos técnicos ni equipo costoso.
          </p>
        </section>

        <section>
          <h2>¿Qué es un tour virtual 360° creado desde el móvil?</h2>
          <p>
            Un tour virtual 360° creado desde el móvil es una experiencia
            inmersiva e interactiva que permite a los visitantes explorar un
            espacio como si estuvieran físicamente allí. Con tecnología IA, tu
            smartphone captura varias fotos que se ensamblan automáticamente en
            escenas panorámicas homogéneas.
          </p>
          <h3>Características clave</h3>
          <ul>
            <li>
              <strong>Vista 360°</strong> — mira en cualquier dirección,
              incluidas arriba y abajo.
            </li>
            <li>
              <strong>Navegación interactiva</strong> — muévete entre
              habitaciones.
            </li>
            <li>
              <strong>Sin cámara especial</strong> — tu smartphone es el único
              equipo necesario.
            </li>
            <li>
              <strong>Procesamiento IA</strong> — la tecnología hace todo el
              trabajo técnico automáticamente.
            </li>
            <li>
              <strong>Visualización web</strong> — accesible desde cualquier
              navegador sin app.
            </li>
          </ul>
        </section>

        <section>
          <h2>Tu primer tour virtual en 5 pasos</h2>

          <h3>1. Descarga Travvir</h3>
          <p>
            Descarga la app Travvir para empezar tu prueba gratuita en App Store y Google Play. El registro tarda
            menos de 2 minutos. La prueba gratuita cubre todo lo esencial para tu primer tour.
          </p>

          <h3>2. Prepara el espacio</h3>
          <p>
            Ordena la habitación, abre las persianas, enciende todas las luces.
            Un espacio bien iluminado y ordenado produce un tour más
            convincente. Si tienes trípode, colócalo en el centro de la
            habitación.
          </p>

          <h3>3. Captura el panorama</h3>
          <p>
            Abre Travvir y pulsa "Nuevo proyecto". Sigue las instrucciones en
            pantalla: la app te guiará en cada dirección. Gira lento y
            regularmente. Capturar una habitación estándar lleva unos 5–10
            minutos.
          </p>

          <h3>4. Deja que la IA ensamble</h3>
          <p>
            Tras la captura, envía las imágenes para su procesamiento. Travvir
            ensambla el panorama 360° en 2–5 minutos. Recibirás una notificación
            cuando tu panorama esté listo.
          </p>

          <h3>5. Publica tu tour</h3>
          <p>
            Organiza los panoramas en el orden lógico de visita, añade hotspots
            de navegación entre habitaciones y publica. Tu tour recibe
            inmediatamente un enlace compartible.
          </p>
        </section>

        <section>
          <h2>Errores comunes a evitar como principiante</h2>
          <ul>
            <li>
              Girar demasiado rápido — deja que la app confirme cada zona antes
              de avanzar.
            </li>
            <li>
              Olvidar una habitación — los visitantes notan los espacios que
              faltan.
            </li>
            <li>
              No probar en móvil antes de publicar — el 70 % de las
              visualizaciones son en smartphone.
            </li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes para principiantes</h2>

          <h3>¿Cuánto tiempo lleva crear el primer tour?</h3>
          <p>
            Cuenta con 1 a 2 horas para tu primer tour, incluyendo preparación,
            captura y publicación. A partir del segundo, el proceso suele llevar
            menos de una hora.
          </p>

          <h3>¿Se puede modificar el tour tras la publicación?</h3>
          <p>
            Sí — puedes añadir hotspots, cambiar el orden de las escenas,
            actualizar los metadatos y volver a publicar en cualquier momento.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/tomar-fotos-360-movil">Cómo Tomar Fotos 360° con el Móvil: Técnica Completa 2026</a></li>
          <li><a href="/blog/es/errores-fotografia-360">10 Errores de Fotografía 360° que Debes Evitar en 2026</a></li>
          <li><a href="/blog/es/que-es-foto-360">¿Qué es una Foto 360°? Guía Completa 2026</a></li>
          <li><a href="/blog/es/guia-tour-virtual-smartphone">Guía Completa: Crear un Tour Virtual 360° con Smartphone 2026</a></li>
          <li><a href="/blog/es/crear-tour-virtual-con-movil">Crear un Tour Virtual con el Móvil: Guía Completa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-360-principiantes",
);

useBlogSeo({
  title:
    "Tour Virtual 360° para Principiantes: Crea tu 1er Tour desde el Móvil | Travvir",
  description:
    "Guía completa para principiantes sobre cómo crear un tour virtual 360° desde tu smartphone con Travvir. 5 pasos simples, sin equipo especial.",
  keywords:
    "tour virtual principiantes, crear tour virtual primera vez, tutorial tour virtual principiante, tour virtual fácil smartphone",
  slug: "tour-virtual-360-principiantes",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Cuánto tiempo lleva crear el primer tour?",
      answer:
        "Cuenta con 1 a 2 horas para tu primer tour. A partir del segundo, el proceso suele llevar menos de una hora.",
    },
    {
      question: "¿Se puede modificar el tour tras la publicación?",
      answer:
        "Sí — hotspots, orden de escenas y metadatos se pueden actualizar y volver a publicar en cualquier momento.",
    },
  ],
});

</script>
